import type { ApiError } from '@/shared/lib/problem-details';

/* ==========================================================================
   Códigos de erro da impressão

   O mesmo texto de `PrintingErrorCodes` na API. O painel classifica as
   recusas pelo `errorCode` do ProblemDetails, e não pelo status: o 409 serve a
   muitas recusas diferentes.
   ========================================================================== */

export const PRINTING_ERRORS = {
  /** 404: loja fora do piloto. A aba some. */
  disabled: 'printing.disabled',
  /** 409: desligada pela plataforma; só as leituras passam. */
  platformDisabled: 'printing.platform_disabled',
  /** 403: tenant sem acesso efetivo (assinatura pendente). */
  accessBlocked: 'printing.access_blocked',
  /** 409: impressão nunca ativada ou desativada pela loja. */
  notActive: 'printing.not_active',
  /** 409: passos pendentes no assistente (`missing`, `categoriesWithoutDestination`). */
  setupIncomplete: 'printing.setup_incomplete',
  stationNameTaken: 'printing.station_name_taken',
  stationLimitReached: 'printing.station_limit_reached',
  /** 409: exclusão de ponto com rotas, sem `moveTo`. */
  stationInUse: 'printing.station_in_use',
  defaultStationRequired: 'printing.default_station_required',
  pairingCodeInvalid: 'printing.pairing_code_invalid',
  pairingCodeExpired: 'printing.pairing_code_expired',
  agentLimitReached: 'printing.agent_limit_reached',
  /** 409: impressora com cupons; o corpo traz `tickets`. */
  printerInUse: 'printing.printer_in_use',
  ticketNotApplicable: 'printing.ticket_not_applicable',
  renderFailed: 'printing.render_failed',
  /** 409: a loja pausou a impressão. */
  paused: 'printing.paused',
  orderNotAccepted: 'printing.order_not_accepted',
  orderCancelled: 'printing.order_cancelled',
  ticketUnassigned: 'printing.ticket_unassigned',
  printerUnavailable: 'printing.printer_unavailable',
  idempotencyKeyReused: 'printing.idempotency_key_reused',
  /** 409: retentável, a transição perdeu a corrida para outra. */
  requestConflict: 'printing.request_conflict',
  /** 409 (change print-agent): o computador já reportou a primeira fase da atualização. */
  agentUpdateInProgress: 'printing.agent_update_in_progress',
  /** 409 (change print-agent): o computador já está na versão-alvo, ou o canal não tem versões. */
  agentUpdateNotNeeded: 'printing.agent_update_not_needed',
  /** 409 (change print-agent): nenhum computador ativo conectado ao hub, no teste de conexão. */
  agentOffline: 'printing.agent_offline',
} as const;

export type PrintingErrorCode = (typeof PRINTING_ERRORS)[keyof typeof PRINTING_ERRORS];

/** O `errorCode` do ProblemDetails, quando há. */
export function printingErrorCode(error: unknown): string | undefined {
  const problem = (error as ApiError | undefined)?.problem;
  const code = problem?.errorCode;
  return typeof code === 'string' ? code : undefined;
}

export function isPrintingError(error: unknown, code: PrintingErrorCode): boolean {
  return printingErrorCode(error) === code;
}

/** 403 do filtro de assinatura do painel: o interceptor já leva à tela de bloqueio. */
export const SUBSCRIPTION_REQUIRED = 'subscription_required';

export function errorStatus(error: unknown): number | undefined {
  const status = (error as ApiError | undefined)?.problem?.status;
  return typeof status === 'number' ? status : undefined;
}

/* ---- Textos que a equipe lê ---- */

export const PLATFORM_DISABLED_TITLE = 'Impressão desativada temporariamente pela equipe do Vapt Menu';

export const PLATFORM_DISABLED_MESSAGE =
  'Impressão desativada temporariamente pela equipe do Vapt Menu: os cupons não estão saindo pelo computador da loja. ' +
  '"Imprimir pelo navegador" continua no detalhe do pedido, e a impressão volta sem a loja precisar fazer nada.';

export const ACCESS_BLOCKED_MESSAGE = 'Impressão suspensa: há uma pendência na assinatura';

export const BROWSER_PRINT_SUGGESTION =
  'Use "Imprimir pelo navegador" no detalhe do pedido enquanto isso.';

/**
 * Recusa de uma ação da aba Impressão ou do assistente. O desligamento geral
 * recebido com a aba já aberta explica o mesmo que o aviso do modo leitura;
 * o resto usa o texto da API, que já vem em linguagem de loja.
 */
export function describePrintingActionError(error: unknown, fallback: string): string {
  switch (printingErrorCode(error)) {
    case PRINTING_ERRORS.platformDisabled:
      return PLATFORM_DISABLED_MESSAGE;
    case PRINTING_ERRORS.accessBlocked:
      return `${ACCESS_BLOCKED_MESSAGE}.`;
    case PRINTING_ERRORS.paused:
      return 'A impressão está pausada: retome a impressão para mandar a página de teste.';
  }
  const detail = (error as ApiError | undefined)?.problem?.detail;
  const status = errorStatus(error) ?? 0;
  if (detail && status >= 400 && status < 500 && status !== 400) return detail;
  return fallback;
}
